import { useState } from 'react'
import type { Accomplishment, Framing } from '../../stores/libraryStore'
import { useLibraryStore } from '../../stores/libraryStore'

interface FramingsPanelProps {
  accomplishment: Accomplishment
  onClose: () => void
}

export default function FramingsPanel({ accomplishment, onClose }: FramingsPanelProps) {
  const {
    generateFramings,
    updateFraming,
    deleteFraming,
    approveFraming,
    dismissFramingReview,
    regenerateFraming,
    framingsLoading,
    selectedAccomplishment,
  } = useLibraryStore()

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editText, setEditText] = useState('')

  const framings = selectedAccomplishment?.framings ?? accomplishment.framings ?? []

  const formatLabel = (label: string) =>
    label.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

  const handleGenerate = async () => {
    await generateFramings(accomplishment.id)
  }

  const handleEdit = (framing: Framing) => {
    setEditingId(framing.id)
    setEditText(framing.text)
  }

  const handleSaveEdit = async () => {
    if (!editingId) return
    await updateFraming(accomplishment.id, editingId, editText)
    setEditingId(null)
    setEditText('')
  }

  const handleDelete = async (framingId: string) => {
    await deleteFraming(accomplishment.id, framingId)
  }

  const handleApprove = async (framingId: string) => {
    await approveFraming(accomplishment.id, framingId)
  }

  const handleDismissReview = async (framingId: string) => {
    await dismissFramingReview(accomplishment.id, framingId)
  }

  const handleRegenerate = async (framingId: string) => {
    await regenerateFraming(accomplishment.id, framingId)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 pt-16 px-4">
      <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl max-h-[80vh] overflow-y-auto">
        <div className="border-b border-stone-100 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-stone-800 text-sm">Perspectives</h2>
            <p className="text-xs text-stone-400 mt-0.5">
              {accomplishment.employer} &middot; {accomplishment.role_title}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-stone-400 hover:text-stone-600 text-lg"
          >
            &times;
          </button>
        </div>

        <div className="px-6 py-5">
          {framings.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-stone-400 mb-3">
                No perspectives yet. Generate them to see different framings of this proof.
              </p>
              <button
                onClick={handleGenerate}
                disabled={framingsLoading}
                className="rounded px-4 py-2 text-xs text-white bg-stone-800 hover:bg-stone-700 disabled:opacity-50"
              >
                {framingsLoading ? 'Generating...' : 'Generate Perspectives'}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {framings.map((framing) => (
                <div
                  key={framing.id}
                  className="rounded border border-stone-200 p-4"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-medium text-stone-700">
                        {formatLabel(framing.label)}
                      </span>
                      {framing.approved && !framing.needs_review && (
                        <span className="rounded-full bg-green-50 px-1.5 py-0.5 text-[10px] text-green-600">
                          Approved
                        </span>
                      )}
                      {framing.needs_review && (
                        <span
                          title="This proof was updated after this perspective was written."
                          className="rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-700 cursor-help"
                        >
                          Proof updated
                        </span>
                      )}
                      {!framing.auto_generated && (
                        <span className="rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] text-blue-600">
                          Edited
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-wrap justify-end">
                      {framing.needs_review && (
                        <>
                          <button
                            onClick={() => handleRegenerate(framing.id)}
                            disabled={framingsLoading}
                            className="rounded px-2 py-0.5 text-[11px] text-stone-600 hover:bg-stone-50 border border-stone-200 disabled:opacity-40"
                          >
                            Regenerate
                          </button>
                          <button
                            onClick={() => handleDismissReview(framing.id)}
                            disabled={framingsLoading}
                            className="rounded px-2 py-0.5 text-[11px] text-stone-400 hover:bg-stone-50 disabled:opacity-40"
                          >
                            Mark as reviewed
                          </button>
                        </>
                      )}
                      {!framing.approved && !framing.needs_review && (
                        <button
                          onClick={() => handleApprove(framing.id)}
                          className="rounded px-2 py-0.5 text-[11px] text-green-600 hover:bg-green-50"
                        >
                          Approve
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(framing)}
                        className="rounded px-2 py-0.5 text-[11px] text-stone-500 hover:bg-stone-50"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(framing.id)}
                        className="rounded px-2 py-0.5 text-[11px] text-red-500 hover:bg-red-50"
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  {editingId === framing.id ? (
                    <div className="space-y-2">
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        rows={3}
                        className="w-full rounded border border-stone-200 px-3 py-1.5 text-sm text-stone-800 focus:border-stone-400 focus:outline-none resize-y"
                      />
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => setEditingId(null)}
                          className="rounded px-2.5 py-1 text-xs text-stone-500 hover:bg-stone-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveEdit}
                          className="rounded px-2.5 py-1 text-xs text-white bg-stone-800 hover:bg-stone-700"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-stone-700">{framing.text}</p>
                  )}
                </div>
              ))}

              <div className="pt-2">
                <button
                  onClick={handleGenerate}
                  disabled={framingsLoading}
                  className="rounded px-3 py-1.5 text-xs text-stone-600 hover:bg-stone-50 border border-stone-200 disabled:opacity-50"
                >
                  {framingsLoading ? 'Generating...' : 'Generate Missing Perspectives'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
